import type * as ContractTypes from '@draftkings/dk-data-layer';
import { MarketWithSelections } from '@draftkings/component-builder';

type ShouldShowScoresArgs = {
    marketsWithSelections: MarketWithSelections[];
    isTeamSwap: boolean;
};

/**
 * Determines if scores should be shown based on market selections.
 *
 * Returns TRUE when:
 * - Event is NOT team swap AND
 * - Selections match one of these patterns:
 *   • Up to 3 selections with Home + Tie + Away outcome types (no duplicates)
 *   • Up to 2 selections with Home + Away outcome types (no duplicates)
 * - Fewer selections than expected is allowed (locked odds buttons shown for missing ones)
 *
 * Returns FALSE when:
 * - Event is team swap
 * - More selections than expected outcome types
 * - Duplicate outcome types found
 * - Unexpected outcome types present
 */
export const shouldShowScores = ({ marketsWithSelections, isTeamSwap }: ShouldShowScoresArgs): boolean => {
    if (isTeamSwap) {
        return false;
    }

    const selections = Array.from(marketsWithSelections[0]?.selections.values() ?? []) as ContractTypes.Selection[];

    if (selectionsMatchOutcomeTypes(selections, ['Home', 'Away'])) {
        return true;
    }

    if (selectionsMatchOutcomeTypes(selections, ['Home', 'Tie', 'Away'])) {
        return true;
    }

    return false;
};

const selectionsMatchOutcomeTypes = (selections: ContractTypes.Selection[], outcomeTypes: string[]): boolean => {
    if (selections.length > outcomeTypes.length) {
        return false;
    }

    const expectedSet = new Set(outcomeTypes.map((type) => type.toUpperCase()));
    const seenTypes = new Set<string>();

    return selections.every((s) => {
        const type = s.outcomeType?.toUpperCase();
        if (!type || !expectedSet.has(type)) {
            return false;
        }
        if (seenTypes.has(type)) {
            return false;
        }
        seenTypes.add(type);
        return true;
    });
};
